import React, { useState } from "react";
import { connect } from "react-redux";
import { Chip, Tooltip, TextField, Typography, InputAdornment } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import dayjs from "dayjs";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import KPTable from "../../../components/KPTable";
import ScientificCouncilUpdateModal from "../ScientificCouncilUpdateModal";
import ScientificCouncilDeleteModal from "../ScientificCouncilDeleteModal";
import HemisCodeConfirmModal from "../../../components/HemisCodeConfirmModal/HemisCodeConfirmModal";

import { updateScientificCouncil, acceptScientificCouncil } from "../../../reducer/scientificCouncilNew/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import { SCIENTIFIC_COUNCIL_NEW } from "../../../constants/selectOptions";
import { notification } from "../../../reducer/home/action";

function ScientificCouncilTable(props) {
  const [editting, setEditting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmingAccept, setConfirmingAccept] = useState(null);

  const handleUpdate = (e) => props.setUpdating(e);
  const handleEditYear = (e) => setEditting(e);

  const handleDelete = (event, e) => {
    setDeleting(e.id);
    setAnchorEl(event.currentTarget);
  };

  const handleChange = (value) => setEditting({ ...editting, dashboardYear: value });

  const handleSubmit = (e) => {
    e.preventDefault();
    props.updateScientificCouncil(editting.id, editting);
    setEditting(null);
  };

  const handleClear = () => {
    props.updateScientificCouncil(editting.id, { ...editting, dashboardYear: null });
    setEditting(null);
  };

  const acceptScientificCouncilItem = async (e) => {
    let result = await props.acceptScientificCouncil(e.id, !e.accepted);
    return result;
  };

  const handleAccept = (e) => {
    // If already accepted (canceling confirmation), no need for modal
    if (e.accepted) {
      acceptScientificCouncilItem(e).then((success) => {
        if (success) {
          props.statisticUnaccepted({ departmentId: 0, profileId: props.profile?.id });
          if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
        }
      });
    } else {
      // If not accepted (confirming), show modal for HemisCode
      setConfirmingAccept(e);
    }
  };

  const handleConfirmAccept = (hemisCode) => {
    // Save the current accepted status before update (default to false if undefined)
    const currentAcceptedStatus = confirmingAccept.accepted === true;
    const itemId = confirmingAccept.id;

    const updatedData = {
      ...confirmingAccept,
      hemisCode: hemisCode,
    };

    // First update the scientific council with hemisCode
    props.updateScientificCouncil(itemId, updatedData).then((updateResult) => {
      // Check for ERR_HEMIS_DUPLICATE error
      if (updateResult && updateResult.error === "ERR_HEMIS_DUPLICATE") {
        props.pushNoti({
          type: "warning",
          message: `Mã CTKH "${hemisCode}" đã tồn tại trong hệ thống. Vui lòng kiểm tra lại!`,
        });
        return;
      }

      if (updateResult && updateResult.success) {
        // Then call accept API with the saved boolean value
        props.acceptScientificCouncil(itemId, !currentAcceptedStatus).then((acceptSuccess) => {
          if (acceptSuccess) {
            props.statisticUnaccepted({ departmentId: 0, profileId: props.profile?.id });
            if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
          }
        });
      }
    });

    setConfirmingAccept(null);
  };

  var list = [];
  if (props.type === "all") {
    if (Array.isArray(props.listScientificCouncil)) {
      props.listScientificCouncil.forEach((e) => list.push(e));
    }
    // console.log("ScientificCouncilTable - type: all, listScientificCouncil:", props.listScientificCouncil);
  } else {
    if (Array.isArray(props.listScientificCouncilById)) {
      props.listScientificCouncilById.forEach((e) => list.push(e));
    }
    // console.log("ScientificCouncilTable - type: other/self, listById:", props.listScientificCouncilById);
  }

  // console.log("ScientificCouncilTable - Final list:", list, "isFetching:", props.isFetching);

  const getCouncilTypeLabel = (value) => {
    const found = SCIENTIFIC_COUNCIL_NEW.councilType.find((e) => e.value === value);
    return found ? found.title : value || "-";
  };

  const columns = [
    {
      title: <b>Tên hội đồng</b>,
      key: "title",
      width: "11%",
      searchable: true,
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: <b>Mã công trình</b>,
      key: "hemisCode",
      width: "9%",
      searchable: true,
      render: (value) =>
        value ? (
          <span style={{ wordBreak: "break-word" }}>{value}</span>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        ),
    },
    {
      title: <b>Năm tính giờ</b>,
      key: "year",
      width: "10%",
      searchable: true,
      render: (value) => (value ? `${value} - ${value + 1}` : "-"),
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
    },
    {
      title: <b>Loại hội đồng</b>,
      key: "type",
      width: "15%",
      searchable: true,
      render: (value) => getCouncilTypeLabel(value),
      type: "include",
      filters: SCIENTIFIC_COUNCIL_NEW.councilType.map((e) => ({ value: e.value, text: e.title })),
      onFilter: (value, row) => row.filter((e) => value.includes(e.type)),
    },
    {
      title: <b>Thời gian thực hiện</b>,
      key: "executionTime",
      width: "12%",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) => dayjs(a.executionTime).unix() - dayjs(b.executionTime).unix(),
    },
    {
      title: <b>Tình trạng</b>,
      key: "accepted",
      width: "12%",
      render: (value) => (
        <Chip
          size="small"
          color={value ? "primary" : "secondary"}
          label={value ? "Đã được duyệt" : "Chưa được duyệt"}
        />
      ),
      type: "include",
      filters: [
        {
          text: "Đã được duyệt",
          value: true,
        },
        {
          text: "Chưa được duyệt",
          value: false,
        },
      ],
      onFilter: (value, row) => row.filter((e) => value.includes(e.accepted)),
    },
    {
      title: <b>Tác vụ</b>,
      key: "action",
      width: "18%",
      render: (value, row) =>
        row?.id !== editting?.id ? (
          <>
            <Typography
              className="table-action-buton"
              variant="caption"
              color="primary"
              onClick={() => handleUpdate(row)}
            >
              Chi tiết
            </Typography>
            {row.locked ? (
              <Typography className="table-action-buton" variant="caption" color="textPrimary">
                {`Đã khóa ${
                  (row.locked === "kpi" && "KPI") ||
                  (row.locked === "reward" && "thưởng") ||
                  (row.locked === "timeResearch" && "giờ nghiên cứu")
                }`}
              </Typography>
            ) : (
              <>
                {(props.type !== "all" || props.role === "sudo-admin") && !row.accepted && (
                  <Typography
                    className="table-action-buton"
                    variant="caption"
                    color="secondary"
                    onClick={(event) => handleDelete(event, row)}
                  >
                    Xoá
                  </Typography>
                )}
                {props.role === "sudo-admin" && (
                  <>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="primary"
                      onClick={(event) => handleAccept(row)}
                    >
                      {row.accepted ? "Hủy xác nhận" : "Xác nhận"}
                    </Typography>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Typography className="table-action-buton" variant="caption" color="primary" onClick={handleSubmit}>
              Lưu
            </Typography>
            <Typography
              className="table-action-buton"
              variant="caption"
              color="secondary"
              onClick={() => setEditting(null)}
            >
              Huỷ
            </Typography>
          </>
        ),
    },
  ];

  return (
    <>
      <KPTable
        shadow="none"
        rowsPerPage={10}
        maxHeight="calc(100vh - 288px)"
        columns={columns}
        rows={list.sort((a) => (!a.accepted ? -1 : 1))}
        loading={props.isFetching}
      />
      <ScientificCouncilUpdateModal
        adminTab={props.adminTab}
        updating={props.updating}
        setUpdating={props.setUpdating}
      />
      <ScientificCouncilDeleteModal deleting={deleting} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
      <HemisCodeConfirmModal
        open={confirmingAccept !== null}
        onClose={() => setConfirmingAccept(null)}
        onConfirm={handleConfirmAccept}
        currentHemisCode={confirmingAccept?.hemisCode || ""}
        title="Xác nhận hội đồng khoa học"
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  profile: select(state, "profileReducer", "detail"),
  isFetching: select(state, "scientificCouncilNewReducer", "isFetching"),
  listScientificCouncil: select(state, "scientificCouncilNewReducer", "listFiltered"),
  listScientificCouncilById: select(state, "scientificCouncilNewReducer", "listById"),
});

const mapDispatchToProps = (dispatch) => ({
  acceptScientificCouncil: (id, accept) => dispatch(acceptScientificCouncil(id, accept)),
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  updateScientificCouncil: (id, payload) => dispatch(updateScientificCouncil(id, payload)),
  pushNoti: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(ScientificCouncilTable));

